import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const pickMember = (data) => {
  if (!data || typeof data !== "object") return null;
  if (data.member && typeof data.member === "object") return data.member;
  if (data.community && typeof data.community === "object")
    return data.community;
  if (
    data.result &&
    typeof data.result === "object" &&
    !Array.isArray(data.result)
  ) {
    return data.result;
  }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  if (data._id || data.id || data.communityId || data.email || data.name)
    return data;
  return null;
};

const getMemberImage = (m) =>
  String(m?.image || m?.profileImage || m?.avatar || "").trim();

const getMemberMobile = (m) =>
  String(m?.mobileNumber || m?.phone || m?.mobile || "").trim();

const getMemberBlock = (m) => String(m?.blockNumber || m?.block || "").trim();

const getMemberStatus = (m) => {
  if (typeof m?.isActive === "boolean")
    return m.isActive ? "active" : "inactive";
  return (
    String(m?.status || "")
      .trim()
      .toLowerCase() || ""
  );
};

const formatDateTime = (iso) => {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const DetailRow = ({ label, value }) => (
  <div className="row py-2 border-bottom" style={{ margin: 0 }}>
    <div
      className="col-12 col-sm-4 text-muted"
      style={{ fontSize: 13, fontWeight: 600 }}
    >
      {label}
    </div>
    <div className="col-12 col-sm-8" style={{ fontSize: 14 }}>
      {value != null && value !== "" ? String(value) : "—"}
    </div>
  </div>
);

const SectionTitle = ({ children, first }) => (
  <div
    className="text-muted text-uppercase"
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      marginTop: first ? 0 : 18,
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const EXECUTIVE_STATUS_LABELS = {
  active: "Active",
  tenure_completion: "Tenure Completion",
  resigned: "Resigned",
  removed: "Removed",
};

const getExecutiveStatus = (m) =>
  String(m?.executiveStatus || "")
    .trim()
    .toLowerCase() || "active";

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const CommunityView = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { id: idParam } = useParams();

  const communityId = useMemo(() => {
    try {
      return idParam ? decodeURIComponent(String(idParam)).trim() : "";
    } catch {
      return String(idParam || "").trim();
    }
  }, [idParam]);

  const userId = useMemo(() => {
    try {
      return String(localStorage.getItem("userid") || "").trim();
    } catch {
      return "";
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState(null);

  useEffect(() => {
    if (!communityId) {
      toast.error("Invalid community member link.");
      navigate("/community", { replace: true });
    }
  }, [communityId, navigate]);

  const fetchMember = useCallback(async () => {
    if (!API_BASE_URL || !communityId) {
      if (!API_BASE_URL)
        toast.error("Missing REACT_APP_API_URL in environment");
      setLoading(false);
      return;
    }

    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const payload = {
        action: "community.getById",
        communityId,
      };
      if (userId) {
        payload.userId = userId;
        payload.userid = userId;
      }

      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        const m = pickMember(data);
        if (m) {
          setMember(m);
        } else {
          toast.error(
            data?.message || "Community member not found in response",
          );
          setMember(null);
        }
      } else {
        toast.error(data?.message || "Failed to load community member");
        setMember(null);
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Something went wrong",
      );
      setMember(null);
    } finally {
      if (didIncrement) loaderDecrement();
      setLoading(false);
    }
  }, [API_BASE_URL, communityId, userId]);

  useEffect(() => {
    fetchMember();
  }, [fetchMember]);

  const name = member?.name || "—";
  const email = member?.email || "—";
  const designation = member?.designation || "—";
  const mobile = getMemberMobile(member) || "—";
  const block = getMemberBlock(member) || "—";
  const flatNumber = member?.flatNumber || "—";
  const image = getMemberImage(member);
  const status = getMemberStatus(member);
  const executiveStatus = getExecutiveStatus(member);
  const executiveLabel =
    EXECUTIVE_STATUS_LABELS[executiveStatus] || executiveStatus;

  const initials =
    String(name !== "—" ? name : "M")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("") || "M";

  return (
    <div className="employee-form">
      <div className="content-header">
        <div
          className="container-fluid d-flex justify-content-between align-items-end flex-wrap"
          style={{ gap: 12 }}
        >
          <div>
            <nav className="text-muted mb-1" style={{ fontSize: 13 }}>
              <Link to="/community">Executive Committee </Link>
              <span className="mx-2">/</span>
              <span>View</span>
            </nav>
            <h1 className="m-0 txt_lft">Executive Committee </h1>
            <p style={{ margin: 0, color: "#6c757d", fontSize: 14 }}>
              View Executive Committee details
            </p>
          </div>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            <Link to="/community" className="btn btn-secondary">
              <i className="fa-solid fa-arrow-left mr-1" /> Back
            </Link>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          {loading && !member ? (
            <div className="card">
              <div className="card-body text-muted">Loading member…</div>
            </div>
          ) : member ? (
            <div className="row">
             <div className="col-12 col-lg-4 mb-3">
               <div className="card" style={{ alignSelf: "flex-start" }}>
                <div className="card-body text-center">
                    <div
                      className="d-inline-flex align-items-center justify-content-center mb-3"
                      style={{
                        width: 110,
                        height: 110,
                        borderRadius: "50%",
                        background: "linear-gradient(145deg, #0094a9, #007a8a)",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 28,
                        overflow: "hidden",
                      }}
                    >
                      {image ? (
                        <img
                          src={image}
                          alt={name}
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        initials
                      )}
                    </div>

                    <h2 className="h4 mb-1">{name}</h2>
                    <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                      {designation !== "—" ? designation : email}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-8 mb-3">
                <div className="card h-100">
                  <div className="card-body">
                    <SectionTitle first>Account</SectionTitle>
                    <div className="border rounded overflow-hidden mb-0">
                      <DetailRow label="Name" value={name} />
                      <DetailRow label="Email" value={email} />
                      <DetailRow label="Mobile" value={mobile} />
                      <DetailRow label="Position" value={designation} />
                      <DetailRow
                        label="Executive status"
                        value={
                          executiveLabel ? executiveLabel.toUpperCase() : ""
                        }
                      />
                    </div>

                    <SectionTitle>Residence</SectionTitle>
                    <div className="border rounded overflow-hidden mb-0">
                      <DetailRow label="Block" value={block} />
                      <DetailRow label="Flat no." value={flatNumber} />
                      <DetailRow
                        label="Date of occupancy"
                        value={formatDate(member?.dateOfOccupancy)}
                      />
                    </div>
                    <SectionTitle>Executive period</SectionTitle>
                    <div className="border rounded overflow-hidden mb-0">
                      <DetailRow
                        label="From date"
                        value={formatDate(
                          member?.fromDate || member?.createdAt,
                        )}
                      />
                      <DetailRow
                        label="Date of period completion"
                        value={formatDate(member?.toDate)}
                      />
                    </div>

                    {(member?.createdAt || member?.updatedAt) && (
                      <>
                        <SectionTitle>Record</SectionTitle>
                        <div className="border rounded overflow-hidden mb-0">
                          {member?.createdAt ? (
                            <DetailRow
                              label="Created at"
                              value={formatDateTime(member.createdAt)}
                            />
                          ) : null}
                          {member?.updatedAt ? (
                            <DetailRow
                              label="Updated at"
                              value={formatDateTime(member.updatedAt)}
                            />
                          ) : null}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-muted">
                Could not load member. Try Refresh or go back to the list.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunityView;
